import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Plane, 
  MapPin, 
  Clock, 
  Users, 
  Calendar,
  PlaneTakeoff,
  PlaneIcon,
  Globe,
  Building,
  Star,
  ArrowRight,
  Filter,
  Search,
  Plus,
  TrendingUp
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AddFlightForm } from '@/components/package-steps/FlightStep/AddFlightForm';
import { PackageFlightsList } from '@/components/package-steps/FlightStep/PackageFlightsList';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAirlines } from '@/hooks/useTasks';
import { useCountries } from '@/hooks/useCountries';
import { useCities } from '@/hooks/useCities';
import { useFetchAirports } from '@/hooks/useAirports';
import type { PackageData } from '@/pages/CreatePackage';

interface FlightBooking {
  id: string;
  client_name: string;
  route: string;
  departure: string;
  arrival: string;
  airline: string;
  class: string;
  status: 'confirmed' | 'pending' | 'cancelled';
  price: number;
  passengers: number;
  created_at: string;
}

interface FlightStats {
  totalBookings: number;
  confirmedFlights: number;
  pendingFlights: number;
  totalRevenue: number;
  popularAirlines: Array<{ name: string; count: number }>;
  popularRoutes: Array<{ route: string; count: number }>;
}

export default function Flights() {
  const [activeTab, setActiveTab] = useState('overview');
  const [flights, setFlights] = useState<PackageData['flights']>([]);
  const [bookings, setBookings] = useState<FlightBooking[]>([]);
  const [stats, setStats] = useState<FlightStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAirline, setFilterAirline] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState<FlightBooking | null>(null);
  
  const { toast } = useToast();
  const { airlines } = useAirlines();
  const { data: countries = [] } = useCountries();
  const { data: cities = [] } = useCities();
  const { data: airports = [] } = useFetchAirports();

  const loadFlightData = async () => {
    try {
      setLoading(true);
      
      // إزالة البيانات الوهمية
      setBookings([]);

      // حساب الإحصائيات
      setStats({
        totalBookings: 0,
        confirmedFlights: 0,
        pendingFlights: 0,
        totalRevenue: 0,
        popularAirlines: [],
        popularRoutes: []
      });

    } catch (error) {
      console.error('Error loading flight data:', error);
      toast({
        title: "خطأ",
        description: "فشل في تحميل بيانات الرحلات الجوية",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFlightData();
  }, []);

  const handleAddFlight = (newFlight: PackageData['flights'][0]) => {
    setFlights(prev => [...prev, newFlight]);
    toast({
      title: "تم بنجاح",
      description: "تمت إضافة الرحلة الجوية بنجاح",
    });
  };

  const handleRemoveFlight = (index: number) => {
    setFlights(prev => prev.filter((_, i) => i !== index));
    toast({
      title: "تم الحذف",
      description: "تمت إزالة الرحلة الجوية",
    });
  };

  const handleUpdateFlight = (index: number, updatedFlight: PackageData['flights'][0]) => {
    setFlights(prev => {
      const newFlights = [...prev];
      newFlights[index] = updatedFlight;
      return newFlights;
    });
    toast({
      title: "تم التحديث",
      description: "تم تحديث بيانات الرحلة",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'مؤكد';
      case 'pending': return 'قيد الانتظار';
      case 'cancelled': return 'ملغي';
      default: return 'غير معروف';
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = booking.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         booking.route.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         booking.airline.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
    const matchesAirline = filterAirline === 'all' || booking.airline === filterAirline;
    
    return matchesSearch && matchesStatus && matchesAirline;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <PlaneTakeoff className="w-8 h-8 text-white" />
            </div>
            إدارة الرحلات الجوية
          </h1>
          <p className="text-gray-600 mt-2">نظام شامل لإدارة حجوزات الرحلات الجوية والطيران</p>
        </div>
      </div>

      {/* Add Flight Form - في البداية */}
      <Card className="w-full bg-gradient-to-br from-blue-50/50 to-sky-50/50 border-2 border-blue-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <Plus className="w-5 h-5" />
            إضافة رحلة طيران جديدة
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AddFlightForm onAddFlight={handleAddFlight} />
        </CardContent>
      </Card>

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-500 rounded-lg">
                  <Plane className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{stats.totalBookings}</p>
                  <p className="text-sm text-blue-700">إجمالي الحجوزات</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.confirmedFlights}</p>
                  <p className="text-sm text-green-700">رحلات مؤكدة</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-500 rounded-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pendingFlights}</p>
                  <p className="text-sm text-yellow-700">قيد الانتظار</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-500 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{stats.totalRevenue.toLocaleString()}</p>
                  <p className="text-sm text-purple-700">إجمالي الإيرادات (ريال)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">نظرة عامة</TabsTrigger>
          <TabsTrigger value="bookings">الحجوزات</TabsTrigger>
          <TabsTrigger value="manage">إدارة الرحلات</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Popular Airlines */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  شركات الطيران الأكثر استخداماً
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats?.popularAirlines.map((airline, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="font-medium">{airline.name}</span>
                      </div>
                      <Badge variant="secondary">{airline.count} رحلة</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Popular Routes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  المسارات الأكثر شعبية
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats?.popularRoutes.map((route, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="font-medium">{route.route}</span>
                      </div>
                      <Badge variant="secondary">{route.count} رحلة</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                إجراءات سريعة
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2"
                  onClick={() => setActiveTab('manage')}
                >
                  <Plus className="w-6 h-6" />
                  إضافة رحلة جديدة
                </Button>
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2"
                  onClick={() => setActiveTab('bookings')}
                >
                  <Search className="w-6 h-6" />
                  البحث في الحجوزات
                </Button>
                 <Button 
                   variant="outline" 
                   className="h-20 flex-col gap-2"
                   onClick={() => setActiveTab('manage')}
                 >
                   <Search className="w-6 h-6" />
                   إدارة الرحلات المحفوظة
                 </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bookings" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5" />
                فلترة الحجوزات
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Input
                    placeholder="البحث في الحجوزات..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="حالة الحجز" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">جميع الحالات</SelectItem>
                      <SelectItem value="confirmed">مؤكد</SelectItem>
                      <SelectItem value="pending">قيد الانتظار</SelectItem>
                      <SelectItem value="cancelled">ملغي</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Select value={filterAirline} onValueChange={setFilterAirline}>
                    <SelectTrigger>
                      <SelectValue placeholder="شركة الطيران" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">جميع الشركات</SelectItem>
                      {airlines?.map(airline => (
                        <SelectItem key={airline.id} value={airline.name_ar}>
                          {airline.name_ar}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('all');
                      setFilterAirline('all');
                    }}
                  >
                    مسح الفلاتر
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bookings List */}
          <div className="grid grid-cols-1 gap-4">
            {filteredBookings.map((booking) => (
              <Card key={booking.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-500 rounded-lg">
                        <Plane className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{booking.client_name}</h3>
                        <p className="text-gray-600 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {booking.route}
                        </p>
                        <p className="text-sm text-gray-500">{booking.airline} - {booking.class}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg">{booking.price.toLocaleString()} ريال</p>
                        <p className="text-sm text-gray-600">{booking.passengers} مسافر</p>
                      </div>
                      <Badge className={getStatusColor(booking.status)}>
                        {getStatusText(booking.status)}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(booking.departure).toLocaleDateString('ar-SA')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(booking.departure).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                       <Button 
                         variant="ghost" 
                         size="sm" 
                         className="text-blue-600"
                         onClick={() => setSelectedBooking(booking)}
                       >
                         عرض التفاصيل
                         <ArrowRight className="w-4 h-4 mr-1" />
                       </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="manage" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Add Flight Form */}
            <div>
              <AddFlightForm onAddFlight={handleAddFlight} />
            </div>

            {/* Flight List */}
            <div>
              <PackageFlightsList
                packageFlights={flights}
                onRemoveFlight={handleRemoveFlight}
                onUpdateFlight={handleUpdateFlight}
              />
            </div>
          </div>
        </TabsContent>

      </Tabs>

      {/* Dialog لعرض تفاصيل الحجز */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5" />
              تفاصيل الحجز - {selectedBooking?.client_name}
            </DialogTitle>
            <DialogDescription>
              تفاصيل كاملة عن حجز الطيران
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">معلومات العميل</h4>
                  <p><span className="font-medium">الاسم:</span> {selectedBooking.client_name}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">حالة الحجز</h4>
                  <Badge className={getStatusColor(selectedBooking.status)}>
                    {getStatusText(selectedBooking.status)}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">تفاصيل الرحلة</h4>
                  <div className="space-y-1">
                    <p><span className="font-medium">المسار:</span> {selectedBooking.route}</p>
                    <p><span className="font-medium">شركة الطيران:</span> {selectedBooking.airline}</p>
                    <p><span className="font-medium">الدرجة:</span> {selectedBooking.class}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">التوقيتات</h4>
                  <div className="space-y-1">
                    <p><span className="font-medium">المغادرة:</span> {new Date(selectedBooking.departure).toLocaleString('ar-SA')}</p>
                    <p><span className="font-medium">الوصول:</span> {new Date(selectedBooking.arrival).toLocaleString('ar-SA')}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">تفاصيل مالية</h4>
                  <p><span className="font-medium">السعر:</span> {selectedBooking.price.toLocaleString()} ريال</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">عدد المسافرين</h4>
                  <p><span className="font-medium">المسافرون:</span> {selectedBooking.passengers} شخص</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-700 mb-2">تاريخ الإنشاء</h4>
                <p>{new Date(selectedBooking.created_at).toLocaleDateString('ar-SA')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}